import React, { useContext, useMemo } from 'react';
import moment from 'moment';
import t from 'react-translate';
import { css } from '@emotion/react';
import { Button } from 'react-bootstrap';
import { AngularContext, AngularServicesContext } from 'react-app';
import { useSelector } from 'react-redux';
import * as humps from 'humps';

import { AutoEnrollmentInJourney, CompletionStats, CourseRegistrationType, PUSHER_JOURNEY_AUTO_ENROLLMENT } from 'redux/schemas/models/course';
import { enrollInJourney, fetchJourney, updateEnrollmentCapacity } from 'redux/actions/learning-journeys';
import { fetchCourseWaitlist, joinWaitlist, abandonWaitlist } from 'redux/actions/enrollment-waitlist';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { getCurrentEnrollment, getCurrentUserId } from 'redux/selectors/users';
import { getCurrentJourney } from 'redux/selectors/learning-journeys';

import { hexToRgbaString, success, primary, white } from 'styles/global_defaults/colors';
import { boldFontWeight, textLargeFontSize } from 'styles/global_defaults/fonts';
import { halfSpacing, quarterSpacing, standardSpacing, threeQuartersSpacing } from 'styles/global_defaults/scaffolding';

import { mergeRefs } from 'shared/react-utils';
import pusherService from 'shared/services/pusher-service';
import { HomeContext } from 'learning_journeys/components/learning-journey';
import NvIcon from 'shared/components/nv-icon';
import { useAppDispatch } from 'redux/store';
import LineDivider from './divider';
import { config } from '@config/pendo.config.json';
import { CompletionCriteria } from './completion-criteria-dropdown';

enum Status {
  USER_BANNED,
  START_JOURNEY_CTA,
  ENROLLMENT_LIMIT,
  ClOSED_JOURNEY,
  CLOSED_ENROLLMENT,
  NO_MATCH_ENTITLEMENTS,
  COMPLETION_PROGRESS,
  COMPLETED_WITHOUT_CERTIFICATE,
  COMPLETED_WITH_CERTIFICATE,
}

type Props = {
  isStatic?: boolean,
  className?: string,
  condensed?: boolean,
};

const LearningStatus = React.forwardRef<HTMLDivElement, Props>((props, ref) => {
  const {
    className,
    isStatic = false,
    condensed = false,
  } = props;
  const dispatch = useAppDispatch();
  const containerRef = React.useRef<HTMLDivElement>();
  const { setScrollStatusTrigger } = useContext(HomeContext);
  const { injectServices } = React.useContext(AngularContext);
  const [$stateParams, CurrentUserManager, CurrentPermissionsManager] = injectServices(['$stateParams', 'CurrentUserManager', 'CurrentPermissionsManager']);
  const { $state, $scope, $timeout } = useContext(AngularServicesContext);
  const { catalogId } = $state.params;
  const userId = useSelector(getCurrentUserId);
  const currentJourney = useSelector(getCurrentJourney);
  const currentEnrollment = useSelector(getCurrentEnrollment);
  const userCourse = useSelector((state) => state.models.enrollments[currentJourney?.userCourse]);
  const [listeningEnrollments, setListeningEnrollments] = React.useState(true);
  const [hasJoinedWaitlist, setHasJoinedWaitlist] = React.useState<boolean>();

  const [isEnrolling, setIsEnrolling] = React.useState(false);
  // currentEnrollment updates immediately after enrollInJourney (via user.enrollmentIds).
  // userCourse only updates after fetchJourney re-fetches the journey model.
  // Using either being truthy means the learner is enrolled.
  const hasNeverEnrolledInJourney = !currentEnrollment && !userCourse;

  const styles = css`
    background-color: ${hexToRgbaString(white, 0.9)};

    .gradient-bg {
      width: 100%;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      background: linear-gradient(315deg, rgba(220, 253, 253, 0.3) 0%, rgba(247, 251, 232, 0.3) 99%);
    }

    .status-container {
      max-width: 815px;
      padding: ${standardSpacing}px;
    }

    .status-message {
      font-weight: ${boldFontWeight};
      text-align: center;
    }

    .enrollment-status {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: center;
      flex-wrap: wrap;

      > * {
        &:not(:first-child) {
          margin-left: ${quarterSpacing}px;
        }
      }

      .remove-link {
        cursor: pointer;
      }
    }

    .progress-status-container {
      display: flex;
      flex-direction: column;
      align-items: center;

      .progress-title {
        font-size: ${textLargeFontSize}px;
        margin-bottom: ${halfSpacing}px;
      }

      .progress-status {
        font-weight: ${boldFontWeight};
      }
    }

    .completed-certificate-container {
      display: flex;
      flex-direction: column;
      align-items: center;

      .badge-container {
        display: flex;
        align-items: center;
        margin-bottom: ${standardSpacing}px;

        i {
          margin: 0 ${threeQuartersSpacing}px;
          color: ${success}
        }
      }

      .completed-certificate-message {
        font-weight: ${boldFontWeight};

        a {
          color: ${primary};
        }
      }
    }
  `;

  React.useEffect(() => {
    if (isStatic) {
      const { offsetTop, offsetHeight } = containerRef.current;

      setScrollStatusTrigger(offsetTop + offsetHeight);
    }
  }, [isStatic, setScrollStatusTrigger]);

  React.useEffect(() => {
    if (!currentJourney?.id) return () => {};

    const channel = pusherService.initializeCourseChannel(currentJourney.id);
    if (!channel) return () => {};

    const onEnrollmentCapacityChanged = (data: { enrollment_limit: number; enrolled_count: number }) => {
      const camelizedData = humps.camelizeKeys(data) as unknown as { enrollmentLimit: number; enrolledCount: number };
      dispatch(updateEnrollmentCapacity({
        catalogId: currentJourney.catalogId,
        enrollmentLimit: camelizedData.enrollmentLimit,
        enrolledStudentsCount: camelizedData.enrolledCount,
      }));
    };

    channel.bind('enrollment-capacity-changed', onEnrollmentCapacityChanged);

    return () => {
      channel.unbind('enrollment-capacity-changed', onEnrollmentCapacityChanged);
    };
  }, [currentJourney?.id, currentJourney?.catalogId]);

  React.useEffect(() => {
    const identifier = CurrentUserManager.user.anonymizedIdentifier.substr(0, 10);
    pusherService.setupUserChannel(identifier);
    const channel = pusherService.currentUserChannel();

    if (currentJourney?.autoEnrollment) {
      channel.bind(PUSHER_JOURNEY_AUTO_ENROLLMENT, (pusherData: AutoEnrollmentInJourney) => {
        pusherData = humps.camelizeKeys(pusherData) as AutoEnrollmentInJourney;

        setListeningEnrollments(false);

        // Getting up-to-date enrollments when the journey has the auto-enrollment feature enabled
        if (currentJourney.autoEnrollment) {
          CurrentUserManager.refreshUserCourse(pusherData.courseId);
        }
      });
    } else {
      channel.unbind(PUSHER_JOURNEY_AUTO_ENROLLMENT);
    }

    if (!listeningEnrollments) {
      channel.unbind(PUSHER_JOURNEY_AUTO_ENROLLMENT);
    }

    return () => {
      channel.unbind(PUSHER_JOURNEY_AUTO_ENROLLMENT);
    };
  }, [listeningEnrollments, currentJourney?.autoEnrollment]);

  const shouldShowEnrollmentLimitStatus = useMemo(() => {
    const {
      closeDate,
      matchingEntitlements,
      typeOfRegistration,
      enrollmentLimit,
      enrolledStudentsCount = 0,
    } = currentJourney || {};

    const isClosedEnrollment = typeOfRegistration === CourseRegistrationType.CLOSED_ENROLLMENT;
    const isEntitlementBasedEnrollment = typeOfRegistration === CourseRegistrationType.OPEN_BASED_ON_ENTITLEMENTS;
    const isJourneyClosed = closeDate && moment(closeDate) < moment();
    const isLearner = CurrentPermissionsManager?.isLearner?.();

    return !(isEntitlementBasedEnrollment && !matchingEntitlements)
    && !!enrollmentLimit
    && enrolledStudentsCount >= enrollmentLimit
    && hasNeverEnrolledInJourney
    && !isClosedEnrollment
    && !isJourneyClosed
    && isLearner
    && !isEnrolling;
  }, [currentJourney, hasNeverEnrolledInJourney, CurrentPermissionsManager, isEnrolling]);

  React.useEffect(() => {
    if (!shouldShowEnrollmentLimitStatus || !userId) {
      setHasJoinedWaitlist(undefined);
      return;
    }

    dispatch(fetchCourseWaitlist({
      catalogId: currentJourney.catalogId,
      userId: userId?.toString(),
    })).then((result: { payload?: any[] }) => {
      const payload = result?.payload;
      setHasJoinedWaitlist((payload || []).length > 0);
    });
  }, [dispatch, shouldShowEnrollmentLimitStatus, currentJourney?.catalogId, userId]);

  const webviewDismiss = () => {
    if ($stateParams.mobileapp) {
      const stateParams = { ...$stateParams };
      stateParams.webview_dismiss = true;
      $state.go($state.current.name, stateParams);
    }
  };

  const enroll = () => {
    setIsEnrolling(true);
    dispatch(enrollInJourney({ catalogId })).unwrap().then((enrollment) => {
      if ($stateParams.mobileapp) {
        webviewDismiss();
        return;
      }

      dispatch(fetchJourney({ catalogId: currentJourney.catalogId }));
      setListeningEnrollments(true);
      $timeout(() => {
        $scope.CurrentUserManager.user?.enrollments?.push(enrollment);
        $scope.CurrentUserManager.user?.enrollmentIds?.push(enrollment.id);
      });
    }).catch(webviewDismiss).finally(() => {
      setIsEnrolling(false);
    });
  };

  const joinJourneyWaitlist = () => {
    dispatch(joinWaitlist(currentJourney.catalogId)).then((action) => {
      if (!action.error) {
        dispatch(addAlertMessage({
          type: AlertMessageType.SUCCESS,
          header: t.FORM.SUCCESS_BANG(),
          message: t.FLYER.COURSE.ACTION.YOU_ARE_WAITLISTED(),
        }));
        setHasJoinedWaitlist(true);
      }
    });
  };

  const abandonJourneyWaitlist = () => {
    dispatch(abandonWaitlist(currentJourney.catalogId)).then((action) => {
      if (!action.error) {
        dispatch(addAlertMessage({
          type: AlertMessageType.SUCCESS,
          header: t.FORM.SUCCESS_BANG(),
          message: t.FLYER.COURSE.ACTION.YOU_ARE_REMOVED_FROM_WAITLIST(),
        }));
        setHasJoinedWaitlist(false);
      }
    });
  };

  const getStatus = () => {
    const {
      closeDate,
      canTakeCourse,
      matchingEntitlements,
      completionStats,
      typeOfRegistration,
      registrationClosed,
      soaCertificateEnabled,
    } = currentJourney || {};

    const isJourneyCompleted = !!currentEnrollment?.completionStatus
      || (completionStats?.required > 0 && completionStats.collections.every(({ completed, required }) => completed >= required));

    const currentMoment = moment();
    const isJourneyClosed = closeDate && moment(closeDate) < currentMoment;

    const isPublicEnrollment = typeOfRegistration === CourseRegistrationType.FREE_ENROLLMENT
      || typeOfRegistration === CourseRegistrationType.PAID_ENROLLMENT
      || typeOfRegistration === CourseRegistrationType.FREE_AND_PAID_CERTIFICATE_ENROLLMENT
      || typeOfRegistration === CourseRegistrationType.FREE_ENROLLMENT_IN_INSTITUTION;
    const isClosedEnrollment = typeOfRegistration === CourseRegistrationType.CLOSED_ENROLLMENT;
    const isEntitlementBasedEnrollment = typeOfRegistration === CourseRegistrationType.OPEN_BASED_ON_ENTITLEMENTS;


    // User has signed up before
    if (userCourse) {
      // banned
      if (userCourse.banned) return Status.USER_BANNED;
      // !banned or !dropped
      if (!(userCourse.banned || userCourse.dropped)) {
        // Journey completed
        if (isJourneyCompleted) {
          // Certificate enabled
          if (soaCertificateEnabled || userCourse?.credlyBadge) return Status.COMPLETED_WITH_CERTIFICATE;
          // Certificate not enabled
          return Status.COMPLETED_WITHOUT_CERTIFICATE;
        }
        // Journey in progres
        return Status.COMPLETION_PROGRESS;
      }
    }
    // User has not signed up before
    if (!userCourse) {
      // Journey concluded
      if (isJourneyClosed) return Status.ClOSED_JOURNEY;
      // Journey not closed
      // Type of registrations
      if (isClosedEnrollment) return Status.CLOSED_ENROLLMENT;
      if (shouldShowEnrollmentLimitStatus) return Status.ENROLLMENT_LIMIT;
      if (isPublicEnrollment) {
        // Show enroll CTA
        if (!registrationClosed) return Status.START_JOURNEY_CTA;
      }
      if (isEntitlementBasedEnrollment) {
        if (matchingEntitlements && canTakeCourse) {
          // Show enroll CTA
          if (!registrationClosed) return Status.START_JOURNEY_CTA;
        }
        // No match entitlements
        return Status.NO_MATCH_ENTITLEMENTS;
      }
    }

    return null;
  };

  const status = useMemo(getStatus, [currentJourney, currentEnrollment, userCourse, shouldShowEnrollmentLimitStatus]);

  return (
    <div
      css={styles}
      className={className}
      ref={mergeRefs(ref, containerRef)}
    >
      {status !== null && (
        <div className='gradient-bg'>
          <div className='status-container'>
            {status !== null && {
              [Status.ClOSED_JOURNEY]: <ClosedJourneyMessage />,
              [Status.CLOSED_ENROLLMENT]: <ClosedEnrollment />,
              [Status.START_JOURNEY_CTA]: <StartMyJourneyBtn enroll={enroll} />,
              [Status.ENROLLMENT_LIMIT]: <EnrollmentLimitStatus
                currentJourney={currentJourney}
                hasJoinedWaitlist={hasJoinedWaitlist}
                onJoinWaitlist={joinJourneyWaitlist}
                onAbandonWaitlist={abandonJourneyWaitlist}
                onEnroll={enroll}
                canJoinWaitlist={CurrentUserManager.hasLoggedIn()}
              />,
              [Status.NO_MATCH_ENTITLEMENTS]: <NoMatchEntitlements />,
              [Status.USER_BANNED]: <UserBanned teamingTeamAlias={currentJourney.teachingTeamName.downcasedSingularized} />,
              [Status.COMPLETION_PROGRESS]: <CompletionProgress completionStats={currentJourney.completionStats} condensed={condensed} />,
              [Status.COMPLETED_WITH_CERTIFICATE]: <CompletedWithCertificate angularState={$state} catalogId={catalogId} userId={userId} currentJourney={currentJourney} />,
              [Status.COMPLETED_WITHOUT_CERTIFICATE]: <CompletedWithoutCertificate />,
            }[status]}
          </div>
        </div>
      )}
    </div>
  );
});

const StartMyJourneyBtn = ({ enroll }: { enroll: () => void }) => (
  <Button
    variant='primary'
    onClick={enroll}
    pendo-tag-name={config.pendo.learningJourneys.startMyJourney}
  >
    {t.LEARNING_JOURNEYS.DETAILS.STATUS.START_JOURNEY()}
  </Button>
);

const CompletionProgress = ({ condensed, completionStats }: { condensed: boolean, completionStats: CompletionStats }) => {
  // We should only consider non optional collections in the requiredCoursesCompleted count.
  const nonOptionalCollections = completionStats.collections.filter(collection => collection.completionSettings !== CompletionCriteria.OPTIONAL);
  const requiredCoursesCompleted = nonOptionalCollections.reduce((coursesCompleted, { completed, required }) => coursesCompleted + Math.min(completed, required), 0);

  return (
    <div className='progress-status-container'>
      <span className='progress-title'>
        {!condensed && t.LEARNING_JOURNEYS.DETAILS.STATUS.PROGRESS_TOWARDS_COURSE_COMPLETION()}
      </span>
      <span className='progress-status'>
        {t.LEARNING_JOURNEYS.DETAILS.STATUS.JOURNEY_COMPLETION(requiredCoursesCompleted, completionStats.required)}
      </span>
    </div>
  );
};

const CompletedWithCertificate = ({ angularState, catalogId, userId, currentJourney }: { angularState: any, catalogId: string, userId: number, currentJourney: any }) => {
  let selectCase: 'both' | 'credly' | 'soa' = 'both';
  let credlyLink = '';
  let soaLink = '';

  const userCourse = useSelector((state) => state.models.enrollments[currentJourney.userCourse]);

  if (userCourse?.credlyBadge) {
    selectCase = 'credly';
    credlyLink = userCourse?.credlyBadge;
  }

  if (currentJourney?.soaCertificateEnabled) {
    selectCase = userCourse?.credlyBadge ? 'both' : 'soa';
    soaLink = angularState.href('journey-statement-modal', { catalogId, userId });
  }

  return (
    <div className='completed-certificate-container'>
      <div className='badge-container'>
        <LineDivider className='separator' />
        <NvIcon icon='badge' size='large' />
        <LineDivider className='separator' />
      </div>
      <span className='completed-certificate-message'>
        {t.LEARNING_JOURNEYS.DETAILS.STATUS.COMPLETION_CELEBRATION_WITH_CERTIFICATE(
          credlyLink, selectCase, soaLink,
        )}
      </span>
    </div>
  );
};

const CompletedWithoutCertificate = () => (
  <div className='completed-certificate-container'>
    <div className='badge-container'>
      <LineDivider className='separator' />
      <NvIcon icon='success' size='large' />
      <LineDivider className='separator' />
    </div>
    <span className='completed-certificate-message'>
      {t.LEARNING_JOURNEYS.DETAILS.STATUS.COMPLETION_CELEBRATION_WITHOUT_CERTIFICATE()}
    </span>
  </div>
);

const NoMatchEntitlements = () => (
  <div className='status-message'>
    {t.LEARNING_JOURNEYS.DETAILS.STATUS.ENTITLEMENTS_NO_MATCH()}
  </div>
);

const ClosedJourneyMessage = () => (
  <div className='status-message'>
    {t.LEARNING_JOURNEYS.DETAILS.STATUS.JOURNEY_CONCLUDED()}
  </div>
);

const ClosedEnrollment = () => (
  <div className='status-message'>
    {t.LEARNING_JOURNEYS.DETAILS.STATUS.CLOSED_ENROLLMENT()}
  </div>
);

const UserBanned = ({ teamingTeamAlias }: { teamingTeamAlias: string }) => (
  <div className='status-message'>
    {t.FLYER.COURSE.ACTION.BANNED({ teamingTeamAlias })}
  </div>
);

const EnrollmentLimitStatus = ({
  currentJourney,
  hasJoinedWaitlist,
  onJoinWaitlist,
  onAbandonWaitlist,
  onEnroll,
  canJoinWaitlist,
}: {
  currentJourney: any,
  hasJoinedWaitlist: boolean | undefined,
  onJoinWaitlist: () => void,
  onAbandonWaitlist: () => void,
  onEnroll: () => void,
  canJoinWaitlist: boolean,
}) => {
  const enrolledStudentsCount = currentJourney?.enrolledStudentsCount ?? 0;
  const enrollmentLimit = currentJourney?.enrollmentLimit ?? 0;
  const limitReached = enrolledStudentsCount >= enrollmentLimit;
  const canEnroll = !currentJourney?.registrationClosed && !limitReached;
  const waitlistLinkText = t.LEARNING_JOURNEYS.DETAILS.STATUS.WAITLIST_PROMPT_LINK();
  const [waitlistPromptPrefix, waitlistPromptSuffix] = t.LEARNING_JOURNEYS.DETAILS.STATUS.WAITLIST_PROMPT(waitlistLinkText).split(waitlistLinkText);

  return (
    <div className='status-message enrollment-status'>
      <span>
        {t.FLYER.COURSE.ACTION.ENROLLED_STATUS(enrolledStudentsCount, String(enrollmentLimit))}
      </span>
      {limitReached && hasJoinedWaitlist !== undefined && !hasJoinedWaitlist && (
        <span>{t.FLYER.COURSE.ACTION.REGISTRATION_LIMIT_REACHED()}</span>
      )}
      {limitReached && currentJourney?.enrollmentWaitlistEnabled && hasJoinedWaitlist !== undefined && !hasJoinedWaitlist && canJoinWaitlist && (
        <span>
          <React.Fragment>
            {waitlistPromptPrefix}
            <a className='color-primary remove-link' role='button' onClick={onJoinWaitlist}>
              {waitlistLinkText}
            </a>
            {waitlistPromptSuffix}
          </React.Fragment>
        </span>
      )}
      {hasJoinedWaitlist && (
        <span>
          {t.FLYER.COURSE.ACTION.YOU_JOINED_WAITLIST()}
          {' '}
          <a className='color-primary remove-link' role='button' onClick={onAbandonWaitlist}>
            {t.FLYER.COURSE.ACTION.ABANDON_WAITLIST()}
          </a>
        </span>
      )}
      {canEnroll && (
        <Button
          variant='primary'
          onClick={onEnroll}
          pendo-tag-name={config.pendo.learningJourneys.startMyJourney}
        >
          {t.LEARNING_JOURNEYS.DETAILS.STATUS.START_JOURNEY()}
        </Button>
      )}
    </div>
  );
};

export default LearningStatus;
